import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { resolveAccessEntryTypeIcon, resolveInviteEntryTypeIcon } from './accessEntryTypeIcons';
import type { MemberAccessEntryTypeOption } from './api';

type IconTone = 'default' | 'selected' | 'muted';

const TONE_CLASS: Record<IconTone, string> = {
  default: 'bg-primary-100 text-primary-700',
  selected: 'bg-primary-100 text-primary-700',
  muted: 'bg-sunken text-ink-muted',
};

interface AccessEntryTypeIconProps {
  /** Direct Lucide icon override (skips entry-type lookup). */
  icon?: LucideIcon;
  entryType?: Pick<MemberAccessEntryTypeOption, 'portal_icon_class' | 'code' | 'name'> | null;
  accessEntryTypeId?: string | null;
  entryTypes?: MemberAccessEntryTypeOption[];
  tone?: IconTone;
  className?: string;
  iconClassName?: string;
}

/** Circular invite / entry-type glyph used in widget rows and access-invites lists. */
export function AccessEntryTypeIcon({
  icon,
  entryType,
  accessEntryTypeId,
  entryTypes = [],
  tone = 'default',
  className,
  iconClassName,
}: AccessEntryTypeIconProps): JSX.Element {
  const Icon =
    icon ??
    (accessEntryTypeId != null
      ? resolveInviteEntryTypeIcon(accessEntryTypeId, entryTypes)
      : resolveAccessEntryTypeIcon(entryType));

  return (
    <span
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-pill',
        TONE_CLASS[tone],
        className,
      )}
    >
      <Icon className={cn('h-5 w-5', iconClassName)} strokeWidth={1.75} />
    </span>
  );
}

/** Smaller glyph for home widget quick-invite tiles (matches prior h-11 sizing). */
export function AccessEntryTypeQuickIcon({
  entryType,
  className,
}: {
  entryType: Pick<MemberAccessEntryTypeOption, 'portal_icon_class' | 'code' | 'name'>;
  className?: string;
}): JSX.Element {
  const Icon = resolveAccessEntryTypeIcon(entryType);
  return (
    <span
      className={cn(
        'flex h-11 w-11 items-center justify-center rounded-pill bg-primary-50 text-primary-700 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]',
        className,
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
  );
}
